import { useEffect, useState } from "react";
import { Loader2, Trash2, Download, Mail } from "lucide-react";
import api, { formatApiErrorDetail } from "./adminApi";
import { toast } from "sonner";

export default function AdminNewsletter() {
  const [items, setItems] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/admin/newsletter");
      setItems(r.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const remove = async (id) => {
    if (!window.confirm("Apagar esta subscrição?")) return;
    setBusyId(id);
    try {
      await api.delete(`/admin/newsletter/${id}`);
      setItems((cur) => (cur ? cur.filter((x) => x.id !== id) : cur));
      toast.success("Subscrição apagada.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  const exportCsv = () => {
    if (!items || items.length === 0) return;
    const rows = [["email", "created_at", "confirmed"], ...items.map((x) => [x.email, x.created_at, x.confirmed ? "yes" : "no"])];
    const csv = rows.map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `newsletter-magnolia-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (items === null) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-gold" />
      </div>
    );
  }

  return (
    <div data-testid="admin-newsletter-page">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl text-white">Newsletter</h1>
          <p className="mt-1 text-sm text-white/50">
            {items.length} subscritor{items.length === 1 ? "" : "es"} · das subscrições em /promocoes.
          </p>
        </div>
        <button
          disabled={items.length === 0}
          data-testid="newsletter-export"
          onClick={exportCsv}
          className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2 text-xs uppercase tracking-widest text-white/70 transition hover:text-white disabled:opacity-40"
        >
          <Download className="h-4 w-4" strokeWidth={1.5} />
          Exportar CSV
        </button>
      </div>

      {items.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-12 text-center text-sm text-white/50">
          Sem subscrições ainda.
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03]">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-white/10 text-[10px] uppercase tracking-widest text-white/40">
              <tr>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Data</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {items.map((x) => (
                <tr
                  key={x.id}
                  data-testid={`newsletter-${x.id}`}
                  className="border-b border-white/5 last:border-b-0 hover:bg-white/[0.02]"
                >
                  <td className="px-4 py-3">
                    <a href={`mailto:${x.email}`} className="inline-flex items-center gap-2 text-white/85 hover:text-gold">
                      <Mail className="h-3.5 w-3.5 text-gold/70" strokeWidth={1.5} />
                      {x.email}
                    </a>
                  </td>
                  <td className="px-4 py-3 text-white/60">
                    {x.created_at ? new Date(x.created_at).toLocaleString("pt-PT") : "—"}
                  </td>
                  <td className="px-4 py-3">
                    {x.seen ? (
                      <span className="rounded-full border border-white/15 px-2 py-0.5 text-[10px] uppercase tracking-widest text-white/50">
                        vista
                      </span>
                    ) : (
                      <span className="rounded-full border border-gold/40 bg-gold/15 px-2 py-0.5 text-[10px] uppercase tracking-widest text-gold">
                        nova
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      disabled={busyId === x.id}
                      data-testid={`newsletter-delete-${x.id}`}
                      onClick={() => remove(x.id)}
                      className="inline-flex items-center gap-1.5 rounded-full border border-red-400/30 px-3 py-1 text-[11px] uppercase tracking-widest text-red-300/80 transition hover:bg-red-500/10 hover:text-red-200 disabled:opacity-40"
                    >
                      <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                      Apagar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
